import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../canopy-ui-components/api';
import { logout as AuthLogout } from '../../canopy-ui-components/api/auth';
import {
    getUserPreferences,
    updateUserPreferences,
} from '../../canopy-ui-components/api/preference';
import { getSwitchUser } from '../../canopy-ui-components/helpers/localStorage';
import { RootState } from '../store';

export interface IUserPreferences {
    app?: string;
    locale?: string;
}

interface Auth {
    user: any;
    logoutStatus: string;
    switchUser?: any;
    isSessionValid: boolean | null;
    userPreferences: {
        loading: boolean;
        data?: IUserPreferences;
        error: boolean;
    };
}

const initialState: Auth = {
    user: apiClient?.session,
    logoutStatus: '',
    switchUser: getSwitchUser(),
    isSessionValid: null,
    userPreferences: {
        loading: false,
        data: undefined,
        error: false,
    },
};

export const logout = createAsyncThunk(
    'auth/user',
    async (data, { rejectWithValue }) => {
        try {
            const response: any = await AuthLogout();
            return await response?.json();
        } catch (e) {
            return rejectWithValue(1);
        }
    }
);

export const userPreferencesThunk = createAsyncThunk(
    'users/preferences',
    async () => {
        const res = await getUserPreferences();
        return res;
    }
);

export const updateUserPreferencesThunk = createAsyncThunk(
    'update/users/preferences',
    async (preferences: any) => {
        const res = await updateUserPreferences(preferences);
        return res;
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState: initialState,
    reducers: {
        setUser(state: Auth, action: any) {
            state.user = action.payload;
        },
        setSwitchUser(state: Auth, action: any) {
            state.switchUser = action.payload;
        },
        setIsSessionValid(state: Auth, action: any) {
            state.isSessionValid = action.payload;
        },
    },
    extraReducers: {
        [logout.pending.type]: (state) => {
            state.logoutStatus = 'loading';
        },
        [logout.fulfilled.type]: (state, action: PayloadAction<any>) => {
            state.logoutStatus = 'success';
            state.user = action.payload;
        },
        [logout.rejected.type]: (state) => {
            state.logoutStatus = 'error';
            state.user = null;
        },
        [userPreferencesThunk.pending.type]: (state) => {
            state.userPreferences.loading = true;
        },
        [userPreferencesThunk.fulfilled.type]: (
            state,
            action: PayloadAction<any>
        ) => {
            state.userPreferences = {
                loading: false,
                data: action.payload,
                error: false,
            };
        },
        [userPreferencesThunk.rejected.type]: (
            state,
            action: PayloadAction<any>
        ) => {
            state.userPreferences = {
                loading: false,
                data: undefined,
                error: true,
            };
        },
        [updateUserPreferencesThunk.fulfilled.type]: (
            state,
            action: PayloadAction<any>
        ) => {
            state.userPreferences.data
                ? (state.userPreferences.data.locale = action.payload.locale)
                : (state.userPreferences.data = action.payload);
        },
    },
});

export const selectAuth = (state: RootState): Auth => state.auth;

export const selectUserPreferences = (
    state: RootState
): {
    loading: boolean;
    data?: IUserPreferences;
    error: boolean;
} => state.auth?.userPreferences;

export const { setUser, setSwitchUser, setIsSessionValid } = authSlice.actions;

export default authSlice;
